// The Summary's "Details": the day's arithmetic, spelled out — where the
// budget comes from and where the burn comes from (owner request: the old
// link to Progress told you nothing). Pure display over TodayResponse.
import { useT } from "../lib/i18n";
import { SheetShell } from "./SheetShell";
import type { TodayResponse } from "@shared/types";

export function DayDetailsSheet({ day, onClose }: { day: TodayResponse; onClose: () => void }) {
  const t = useT();
  const manual = day.activities.filter((a) => a.source !== "healthkit");
  const manualKcal = manual.reduce((s, a) => s + a.kcal_burned, 0);
  const health = day.active_kcal;
  const baseline = day.budget_kcal - day.burned_kcal;
  const remaining = Math.max(0, day.budget_kcal - day.consumed.calories);

  const Row = ({ label, value, strong = false, indent = false }: {
    label: string; value: string; strong?: boolean; indent?: boolean;
  }) => (
    <div className={`flex items-baseline gap-2 text-sm ${indent ? "pl-4" : ""}`}>
      <span className={`flex-1 ${strong ? "font-medium" : "text-fg-secondary"}`}>{label}</span>
      <span className={`tabular-nums ${strong ? "font-semibold" : "text-fg-muted"}`}>{value}</span>
    </div>
  );

  return (
    <SheetShell variant="center" onClose={onClose}>
      <div className="space-y-4 pt-1">
        <h2 className="text-lg font-semibold">{t("details.title")}</h2>

        <section className="space-y-1.5">
          <h3 className="text-sm text-fg-muted uppercase tracking-wide">{t("details.budget")}</h3>
          <Row label={t("details.baseline")} value={`${baseline} kcal`} />
          <Row label={t("details.exercise_bonus")} value={`+${day.burned_kcal} kcal`} />
          <div className="border-t border-line pt-1.5">
            <Row label={t("details.budget_total")} value={`${day.budget_kcal} kcal`} strong />
          </div>
        </section>

        <section className="space-y-1.5">
          <h3 className="text-sm text-fg-muted uppercase tracking-wide">{t("details.intake")}</h3>
          <Row label={t("summary.eaten")} value={`${day.consumed.calories} kcal`} />
          <Row label={t("details.remaining")} value={`${remaining} kcal`} strong />
        </section>

        <section className="space-y-1.5">
          <h3 className="text-sm text-fg-muted uppercase tracking-wide">{t("details.burned")}</h3>
          {health != null && <Row label={t("details.from_health")} value={`${health} kcal`} indent />}
          {manual.length > 0 && (
            <Row label={t("details.from_activities", { n: String(manual.length) })} value={`${manualKcal} kcal`} indent />
          )}
          {health == null && manual.length === 0 && (
            <p className="text-sm text-fg-muted">{t("details.no_burn")}</p>
          )}
          <div className="border-t border-line pt-1.5">
            <Row label={t("details.burned_total")} value={`${day.burned_kcal} kcal`} strong />
          </div>
          {health != null && (
            <p className="text-sm text-fg-faint leading-relaxed">{t("details.burn_note")}</p>
          )}
        </section>
      </div>
    </SheetShell>
  );
}
